import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { approvalSettingsProblem, emailProblems, MAX_APPROVAL_EMAILS, settingsToSend } from '../lib/approvalSettings'
import { SETTING_SECTIONS, type BranchSettingsPatch, type BranchSettingsView, type SettingKey, type SettingSection } from '../lib/branchSettings'
import { requestKindLabel } from '../lib/deskStatus'
import { HANDOFF_LABEL_MAX, handoffLabelProblem, handoffWithoutPoint, type BreakReason } from '../lib/orgOptions'
import { explain } from '../lib/staffErrors'
import type { StaffKey } from '../lib/staffText'
import type { ApprovalSettings, Branch, RequestKind, Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { BreakReasons } from './Options'
import { InnerPage, Notice, SettingBlock, SettingRow, SettingsCard, SwitchRow, Tick, type Crumb } from './settingsKit'
import '../styles/orgset.css'

/**
 * A branch's own settings on its page (#branches/<code>): Counter, Breaks,
 * Approvals, Reception and Booking. Each switch saves at once; "Apply to
 * branches…" copies chosen settings to other branches (ApplyPage).
 */

const SECTION_TITLE: Record<SettingSection, StaffKey> = {
  counter: 'org.opt.counterGroup',
  breaks: 'org.opt.breaksGroup',
  approvals: 'admin.section.approvals',
  reception: 'org.opt.receptionGroup',
  booking: 'org.opt.bookingGroup',
}

const SETTING_NAME: Record<SettingKey, StaffKey> = {
  handoff: 'org.opt.handoff',
  outOfTurnCall: 'org.opt.outOfTurnCall',
  outOfTurnReasonRequired: 'bp.set.outOfTurnReason',
  callStartsServing: 'org.opt.callStartsServing',
  breakApproval: 'org.opt.breakApproval',
  breakReasons: 'org.opt.breakReasons',
  approvals: 'admin.section.approvals',
  priorityTickets: 'org.opt.priorityTickets',
  onlineBooking: 'org.opt.onlineBooking',
}

const APPROVAL_KINDS: RequestKind[] = ['lunch', 'short_break', 'task']

export function BranchSettingsPanel({
  branch,
  organisation,
  mayWrite,
  flash,
  onApply,
}: {
  branch: Branch
  organisation?: string
  mayWrite: boolean
  flash: (text: string) => void
  onApply: () => void
}) {
  const { lang, say } = useStaffText()
  const [s, setS] = useState<BranchSettingsView | null>(null)
  const [approvals, setApprovals] = useState<ApprovalSettings | null>(null)
  const [services, setServices] = useState<Service[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    const [read, appr] = await Promise.allSettled([api.branchSettings(branch.id), api.branchApprovals(branch.id)])
    if (read.status === 'fulfilled') setS(read.value.settings)
    else setError(explain(read.reason, lang, 'org.opt.notLoaded'))
    if (appr.status === 'fulfilled') {
      const a = appr.value.settings
      setApprovals({ emails: a.emails ?? [], needsApproval: a.needsApproval ?? [] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch.id])

  useEffect(() => {
    setS(null)
    setApprovals(null)
    void load()
  }, [load])
  useEffect(() => {
    api
      .catalogueServices(organisation)
      .then((r) => setServices(r.services))
      .catch(() => setServices(null))
  }, [organisation])

  const run = async (fn: () => Promise<void>, said: string) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      flash(said)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }
  const save = (change: BranchSettingsPatch, said: string) =>
    void run(async () => setS((await api.saveBranchSettings(branch.id, change)).settings), said)
  const flip = (key: keyof BranchSettingsPatch, name: StaffKey) => (on: boolean) =>
    save({ [key]: on }, say(on ? 'org.opt.on' : 'org.opt.off', { name: say(name) }))
  const saveApprovals = (next: ApprovalSettings, said: string) =>
    void run(async () => {
      const a = (await api.saveBranchApprovals(branch.id, settingsToSend(next))).settings
      setApprovals({ emails: a.emails ?? [], needsApproval: a.needsApproval ?? [] })
    }, said)

  if (!s) return error ? <Notice>{error}</Notice> : null
  const off = !mayWrite || busy
  const orgReasons = s.breakReasons.filter((r) => !r.branchId)
  const ownReasons = s.breakReasons.filter((r) => r.branchId)
  const bookingLicensed = !s.onlineBooking || s.effective.onlineBooking

  return (
    <section className="bp-set" aria-label={say('bp.settings')}>
      <header className="bp-set__head">
        <h2>{say('bp.settings')}</h2>
        {mayWrite && (
          <Button variant="secondary" size="sm" onPress={onApply}>
            <Icon name="copy" size={16} /> {say('bp.set.apply')}
          </Button>
        )}
      </header>
      {error && <Notice>{error}</Notice>}

      <SettingsCard title={say(SECTION_TITLE.counter)}>
        <SwitchRow label={say('org.opt.handoff')} description={say('org.opt.handoffHint')} checked={s.handoff} disabled={off} onChange={flip('handoff', 'org.opt.handoff')} />
        {handoffWithoutPoint(s.handoff, services) && (
          <SettingRow nested control label={<span className="os-warn" role="status">{say('org.opt.noHandoffPoint')}</span>}>
            <Button variant="secondary" size="sm" onPress={() => (window.location.hash = 'services')}>
              {say('org.opt.toServices')}
            </Button>
          </SettingRow>
        )}
        {s.handoff && (
          <HandoffLabel saved={s.handoffLabel} busy={off} onSave={(handoffLabel) => save({ handoffLabel }, say('org.opt.saved'))} />
        )}
        <SwitchRow
          label={say('org.opt.outOfTurnCall')}
          description={say('org.opt.outOfTurnCallHint')}
          checked={s.outOfTurnCall}
          disabled={off}
          onChange={flip('outOfTurnCall', 'org.opt.outOfTurnCall')}
        />
        {s.outOfTurnCall && (
          <SwitchRow nested label={say('org.opt.reasonRequired')} checked={s.outOfTurnReasonRequired} disabled={off} onChange={flip('outOfTurnReasonRequired', 'org.opt.reasonRequired')} />
        )}
        <SwitchRow
          label={say('org.opt.callStartsServing')}
          description={say('org.opt.callStartsServingHint')}
          checked={s.callStartsServing}
          disabled={off}
          onChange={flip('callStartsServing', 'org.opt.callStartsServing')}
        />
      </SettingsCard>

      <SettingsCard title={say(SECTION_TITLE.breaks)}>
        <SettingRow label={say('org.opt.breakReasons')} description={say('org.opt.breakReasonsHint')} />
        {orgReasons.map((r) => (
          <SwitchRow
            key={r.id}
            nested
            label={r.name}
            checked={r.on}
            disabled={off}
            onChange={(on) =>
              save(
                { breakReasonsOff: on ? s.breakReasonsOff.filter((id) => id !== r.id) : [...s.breakReasonsOff, r.id] },
                say(on ? 'org.opt.on' : 'org.opt.off', { name: r.name }),
              )
            }
          />
        ))}
        <SettingRow nested label={<Chip size="sm" variant="secondary">{say('bp.set.own')}</Chip>} />
        <BreakReasons
          heading={false}
          reasons={ownReasons}
          taken={orgReasons}
          mayWrite={mayWrite}
          ops={{
            list: async () => (await api.branchSettings(branch.id)).settings.breakReasons.filter((r) => r.branchId),
            create: (body) => api.createBranchReason(branch.id, body),
            update: (id, body) => api.updateBranchReason(branch.id, id, body),
            remove: (id) => api.deleteBranchReason(branch.id, id),
          }}
          onChanged={(own: BreakReason[]) =>
            setS((x) => (x ? { ...x, breakReasons: [...x.breakReasons.filter((r) => !r.branchId), ...own.map((r) => ({ ...r, on: true }))] } : x))
          }
          flash={flash}
        />
      </SettingsCard>

      {approvals && (
        <SettingsCard title={say(SECTION_TITLE.approvals)}>
          {/* The master switch sits with what it governs; the kinds below only count while it is on. */}
          <SwitchRow
            label={say('org.opt.breakApproval')}
            description={say('org.opt.breakApprovalHint')}
            checked={s.breakApproval}
            disabled={off}
            onChange={flip('breakApproval', 'org.opt.breakApproval')}
          />
          {APPROVAL_KINDS.map((kind) => {
            const label = requestKindLabel(kind, lang)
            const on = approvals.needsApproval.includes(kind)
            return (
              <SwitchRow
                key={kind}
                nested
                label={label}
                checked={on}
                disabled={off || !s.breakApproval}
                onChange={(next) =>
                  saveApprovals(
                    { ...approvals, needsApproval: next ? [...approvals.needsApproval, kind] : approvals.needsApproval.filter((k) => k !== kind) },
                    say(next ? 'org.opt.on' : 'org.opt.off', { name: label }),
                  )
                }
              />
            )
          })}
          <ApprovalEmails saved={approvals.emails} busy={off} onSave={(emails) => saveApprovals({ ...approvals, emails }, say('admin.approvals.saved'))} />
        </SettingsCard>
      )}

      <SettingsCard title={say(SECTION_TITLE.reception)}>
        <SwitchRow
          label={say('org.opt.priorityTickets')}
          description={say('org.opt.priorityTicketsHint')}
          checked={s.priorityTickets}
          disabled={off}
          onChange={flip('priorityTickets', 'org.opt.priorityTickets')}
        />
      </SettingsCard>

      <SettingsCard
        title={say(SECTION_TITLE.booking)}
        aside={
          <Button variant="ghost" size="sm" onPress={() => (window.location.hash = `bookingBranches/${encodeURIComponent(branch.code)}`)}>
            {say('bp.set.bookingSetup')} <Icon name="right" size={14} />
          </Button>
        }
      >
        <SwitchRow
          label={say('org.opt.onlineBooking')}
          description={say('org.opt.onlineBookingHint')}
          tag={
            bookingLicensed ? undefined : (
              <Chip size="sm" variant="secondary">
                {say('org.opt.notLicensed')}
              </Chip>
            )
          }
          checked={s.onlineBooking}
          disabled={off}
          onChange={flip('onlineBooking', 'org.opt.onlineBooking')}
        />
      </SettingsCard>
    </section>
  )
}

function HandoffLabel({ saved, busy, onSave }: { saved: string; busy: boolean; onSave: (label: string) => void }) {
  const { say } = useStaffText()
  const [label, setLabel] = useState(saved)
  useEffect(() => setLabel(saved), [saved])
  const typed = label.trim()
  const bad = handoffLabelProblem(typed)
  return (
    <SettingRow
      as="form"
      onSubmit={(e) => {
        e.preventDefault()
        if (!bad && typed !== saved) onSave(typed)
      }}
      nested
      control
      label={<label htmlFor="bp-handoff-label">{say('org.opt.handoffLabel')}</label>}
    >
      {typed !== saved && (
        <Button type="submit" variant="secondary" size="sm" isDisabled={bad || busy}>
          {say('record.save')}
        </Button>
      )}
      <Input
        id="bp-handoff-label"
        className="os-input"
        value={label}
        disabled={busy}
        maxLength={HANDOFF_LABEL_MAX}
        placeholder={say('counter.toCashier')}
        onChange={(e) => setLabel(e.target.value)}
      />
    </SettingRow>
  )
}

/** The managers emailed about a request: rows typed in place, saved together. */
function ApprovalEmails({ saved, busy, onSave }: { saved: string[]; busy: boolean; onSave: (emails: string[]) => void }) {
  const { say } = useStaffText()
  const [draft, setDraft] = useState(saved)
  // Reset only when the saved list itself changes, so a switch saved meanwhile keeps the typing.
  const savedKey = JSON.stringify(saved)
  useEffect(() => setDraft(JSON.parse(savedKey) as string[]), [savedKey])
  const perEmail = emailProblems(draft)
  const toSend = settingsToSend({ emails: draft, needsApproval: [] })
  const dirty = JSON.stringify(toSend.emails) !== JSON.stringify(saved)
  const problem = approvalSettingsProblem(toSend)
  return (
    <>
      <SettingRow label={say('admin.approvals.emails')} control>
        {dirty && (
          <Button variant="secondary" size="sm" isDisabled={busy || problem !== ''} onPress={() => onSave(toSend.emails)}>
            {say('record.save')}
          </Button>
        )}
      </SettingRow>
      <SettingBlock>
        <div className="bp-set__emails" role="group" aria-label={say('admin.approvals.emails')}>
          {draft.length === 0 && <p className="os-help">{say('settings.approvals.noEmails')}</p>}
          {draft.map((email, i) => (
            <div key={i} className="bp-set__email">
              <Input
                fullWidth
                type="email"
                value={email}
                disabled={busy}
                placeholder={say('admin.approvals.emailPlaceholder')}
                aria-label={say('admin.approvals.emailPlaceholder')}
                aria-invalid={perEmail[i] ? true : undefined}
                onChange={(e) => setDraft(draft.map((x, j) => (j === i ? e.target.value : x)))}
              />
              <Button isIconOnly variant="ghost" size="sm" isDisabled={busy} aria-label={say('common.remove')} onPress={() => setDraft(draft.filter((_, j) => j !== i))}>
                <Icon name="trash" size={16} />
              </Button>
              {perEmail[i] && <small className="appr-err">{say(perEmail[i] as StaffKey)}</small>}
            </div>
          ))}
          {draft.length < MAX_APPROVAL_EMAILS ? (
            <Button variant="ghost" size="sm" isDisabled={busy} onPress={() => setDraft([...draft, ''])}>
              <Icon name="plus" size={16} /> {say('admin.approvals.addEmail')}
            </Button>
          ) : (
            <p className="os-help">{say('settings.approvals.max')}</p>
          )}
        </div>
      </SettingBlock>
    </>
  )
}

/** #branches/<code>/apply: copy chosen settings of this branch to chosen others. */
export function ApplyPage({
  branch,
  crumbs,
  onClose,
  flash,
}: {
  branch: Branch
  crumbs: Crumb[]
  onClose: () => void
  flash: (text: string) => void
}) {
  const { lang, say } = useStaffText()
  const [others, setOthers] = useState<Branch[] | null>(null)
  const [keys, setKeys] = useState<SettingKey[]>([])
  const [targets, setTargets] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    api
      .branches()
      .then((r) => setOthers(r.branches.filter((b) => b.id !== branch.id && b.tenantId === branch.tenantId)))
      .catch((err) => setError(explain(err, lang, 'common.unreachable')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch.id])
  const toggle = <T,>(list: T[], x: T, on: boolean) => (on ? [...list, x] : list.filter((y) => y !== x))
  const all = others ?? []
  return (
    <InnerPage
      before={crumbs}
      title={say('bp.set.applyTitle')}
      onClose={onClose}
      submitLabel={say('bp.set.applyGo')}
      valid={keys.length > 0 && targets.length > 0}
      busy={busy}
      error={error || undefined}
      onSubmit={async () => {
        setBusy(true)
        setError('')
        try {
          await api.applyBranchSettings(branch.id, { settings: keys, branchIds: targets })
          flash(say('bp.set.applied', { n: targets.length }))
          onClose()
        } catch (err) {
          setError(explain(err, lang, 'common.unreachable'))
        } finally {
          setBusy(false)
        }
      }}
    >
      <div className="bp-apply">
        <fieldset className="field">
          <legend>{say('bp.settings')}</legend>
          {SETTING_SECTIONS.map(({ section, keys: inSection }) => (
            <div key={section} className="bp-apply__group">
              <span className="org-sublabel">{say(SECTION_TITLE[section])}</span>
              {inSection.map((k) => (
                <Tick key={k} checked={keys.includes(k)} onChange={(on) => setKeys((x) => toggle(x, k, on))}>
                  {say(SETTING_NAME[k])}
                </Tick>
              ))}
            </div>
          ))}
        </fieldset>
        <fieldset className="field">
          <legend>{say('admin.section.branches')}</legend>
          {others && all.length === 0 && <p className="os-help">{say('bp.set.noOthers')}</p>}
          {all.length > 1 && (
            <Tick
              checked={targets.length === all.length}
              onChange={(on) => setTargets(on ? all.map((b) => b.id) : [])}
            >
              {say('bp.set.allBranches')}
            </Tick>
          )}
          {all.map((b) => (
            <Tick key={b.id} checked={targets.includes(b.id)} onChange={(on) => setTargets((x) => toggle(x, b.id, on))}>
              {b.name} · {b.code}
            </Tick>
          ))}
        </fieldset>
      </div>
    </InnerPage>
  )
}
